import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  PlayCircleFilled as PlayCircleFilledIcon,
  Slideshow as SlideshowIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Компонент для отображения статистики урока
 * @param {Object} lesson - объект урока
 * @param {Object} testProgressInfo - информация о прогрессе тестов
 * @param {string} variant - вариант отображения ('compact' | 'full')
 */
const LessonStats = ({ lesson, testProgressInfo, variant = 'full' }) => {
  const { t } = useTranslation(['courses', 'common']);

  const hasVideo = lesson.type === 'video' || 
    lesson.contentType === 'video' ||
    (lesson.videoUrl && lesson.videoUrl.trim() !== '') ||
    (lesson.contentBlocks && lesson.contentBlocks.length > 0 && lesson.contentBlocks.some(block => 
      block.type === 'video'));

  const hasPresentation = lesson.contentType === 'presentation' ||
    (lesson.contentBlocks && lesson.contentBlocks.length > 0 && lesson.contentBlocks.some(block =>
      block.type === 'presentation'));

  const hasTests = lesson.tests && lesson.tests.length > 0;

  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {hasVideo && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PlayCircleFilledIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {lesson.videoCount || 1} {t('courses:courseDetail.stats.videos')}
            </Typography>
          </Box>
        )}

        {hasPresentation && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SlideshowIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {lesson.slideCount || 1} {t('courses:courseDetail.stats.presentations')}
            </Typography>
          </Box>
        )}

        {hasTests && testProgressInfo && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <QuizIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {testProgressInfo.displayText}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Full variant
  return (
    <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
      {hasVideo && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('courses:courseDetail.stats.videos')}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {lesson.videoCount || 1}
          </Typography>
        </Box>
      )}

      {hasPresentation && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('courses:courseDetail.stats.presentations')}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {lesson.slideCount || 1}
          </Typography>
        </Box>
      )}

      {hasTests && testProgressInfo && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('courses:courseDetail.stats.tests')}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {testProgressInfo.displayText}
          </Typography>
        </Box>
      )}

      {lesson.duration && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('courses:courseDetail.stats.duration')}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {lesson.duration} {t('common:time.shortMin')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LessonStats;
